import React from 'react';
import { MapPin, Home, AlertCircle, Trophy, Medal } from 'lucide-react';
import { Municipality, getMunicipalityRanking, getFeesForYear, getMunicipalityRankingForYear, filterFees } from '@/lib/data';
import { getLatestYear } from '@/config/years';
import { Link } from 'react-router-dom';
import { DropletIcon, FlameIcon, Trash2Icon, HomeIcon, Brush } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useFeeCategories } from '@/contexts/FeeCategoryContext';

interface MunicipalityCardProps {
  municipality: Municipality;
  allMunicipalities?: Municipality[];
  selectedYear?: number;
}

const FEE_ICONS: Record<string, { icon: React.ElementType; bg: string; color: string }> = {
  water: { icon: DropletIcon, bg: 'bg-blue-50', color: 'text-blue-600' },
  sewage: { icon: FlameIcon, bg: 'bg-orange-50', color: 'text-orange-600' },
  waste: { icon: Trash2Icon, bg: 'bg-slate-100', color: 'text-slate-600' },
  property: { icon: HomeIcon, bg: 'bg-slate-100', color: 'text-slate-600' },
  chimney: { icon: Brush, bg: 'bg-amber-50', color: 'text-amber-600' },
};

const MunicipalityCard: React.FC<MunicipalityCardProps> = ({ municipality, allMunicipalities = [], selectedYear }) => {
  const { enabledCategories } = useFeeCategories();
  const displayYear = selectedYear ?? getLatestYear();
  const allFeesToShow = selectedYear ? getFeesForYear(municipality, selectedYear) : municipality.currentFees;
  const feesToShow = filterFees(allFeesToShow, enabledCategories);

  const filteredYearTotal = (year: number) =>
    filterFees(getFeesForYear(municipality, year), enabledCategories).reduce((sum, fee) => sum + fee.amount, 0);

  // Beregn prosentvis endring: foretrekk år-til-år (valgt år vs forrige år), ellers tidligst til nyeste
  const calculatePercentageChange = () => {
    const historicalData = municipality.historicalData;
    if (!historicalData || historicalData.length < 2) {
      return null;
    }
    const sorted = [...historicalData].sort((a, b) => a.year - b.year);
    const prevYear = displayYear - 1;
    const currentYearData = sorted.find(d => d.year === displayYear);
    const prevYearData = sorted.find(d => d.year === prevYear);
    // Foretrekk år-til-år for valgt visningsår
    if (currentYearData && prevYearData) {
      const prevTotal = filteredYearTotal(prevYear);
      if (prevTotal > 0) {
        return {
          percentage: ((filteredYearTotal(displayYear) - prevTotal) / prevTotal) * 100,
          isEstimated: currentYearData.isEstimated || prevYearData.isEstimated,
          fromYear: prevYear,
          toYear: displayYear
        };
      }
    }
    // Fallback: tidligst til nyeste
    const oldestData = sorted[0];
    const newestData = sorted[sorted.length - 1];
    const oldestTotal = filteredYearTotal(oldestData.year);
    if (oldestTotal <= 0) return null;
    return {
      percentage: ((filteredYearTotal(newestData.year) - oldestTotal) / oldestTotal) * 100,
      isEstimated: oldestData.isEstimated || newestData.isEstimated,
      fromYear: oldestData.year,
      toYear: newestData.year
    };
  };

  const percentageChange = calculatePercentageChange();

  // Beregn ranking (for valgt år hvis selectedYear er satt)
  const ranking = allMunicipalities.length > 0
    ? (selectedYear ? getMunicipalityRankingForYear(municipality, allMunicipalities, selectedYear, enabledCategories) : getMunicipalityRanking(municipality, allMunicipalities, enabledCategories))
    : municipality.ranking;

  const hasEstimatedCurrentData = feesToShow.some(fee => fee.isEstimated);
  const totalFees = feesToShow.reduce((sum, fee) => sum + fee.amount, 0);

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('nb-NO') + ' kr';
  };
  
  return (
    <Link to={`/municipality/${municipality.id}`} className="block">
      <div className="h-full bg-white rounded-lg shadow-sm border border-slate-100 p-4 hover:shadow-md hover:border-slate-200 transition-all duration-200">
        <div className="flex justify-between items-start gap-2 mb-4">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-deepCharcoal truncate">{municipality.name}</h3>
            {municipality.county && (
              <p className="text-xs text-warmGray flex items-center gap-1 mt-0.5">
                <MapPin className="h-3 w-3 shrink-0" />
                {municipality.county}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {ranking && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                      ranking === 1 ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                      ranking <= 3 ? 'bg-amber-50 text-amber-700 border border-amber-100' :
                      ranking <= 10 ? 'bg-orange-50 text-orange-700 border border-orange-100' :
                      'bg-slate-50 text-slate-600 border border-slate-100'
                    }`}>
                      {ranking === 1 ? <Trophy className="h-3 w-3" /> : <Medal className="h-3 w-3" />}
                      {ranking === 1 ? '1.' : `#${ranking}`}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Plassering (av {allMunicipalities.length || 'alle'} kommuner)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {percentageChange !== null && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={`text-xs font-medium px-2 py-1 rounded-lg ${
                      percentageChange.percentage > 0 ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-slate-50 text-slate-700 border border-slate-100'
                    }`}>
                      {percentageChange.percentage > 0 ? '+' : ''}{percentageChange.percentage.toFixed(1)}%
                      {percentageChange.isEstimated && <AlertCircle className="h-3 w-3 inline ml-0.5 align-middle" />}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Endring {percentageChange.fromYear}→{percentageChange.toYear}{percentageChange.isEstimated && ' (estimert)'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-3 mb-4">
          <div className="rounded-xl bg-slate-50 p-2.5 shrink-0">
            <Home className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Totalt</p>
            <p className="text-lg font-bold text-deepCharcoal flex items-center gap-1">
              {formatCurrency(totalFees)}
              <span className="text-sm font-normal text-warmGray">/år</span>
              {hasEstimatedCurrentData && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger><AlertCircle className="h-3.5 w-3.5 text-amber-500" /></TooltipTrigger>
                    <TooltipContent><p>Basert på estimerte data</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          {feesToShow.map((fee) => {
            const iconInfo = FEE_ICONS[fee.id];
            if (!iconInfo) return null;
            const FeeIcon = iconInfo.icon;
            return (
              <div key={fee.id} className="flex items-center gap-2">
                <div className={`rounded-lg ${iconInfo.bg} p-1.5 shrink-0`}>
                  <FeeIcon className={`h-3.5 w-3.5 ${iconInfo.color}`} />
                </div>
                <span className="text-sm text-warmGray truncate">{formatCurrency(fee.amount)}</span>
              </div>
            );
          })}
        </div>
        
        {hasEstimatedCurrentData && (
          <p className="mt-3 pt-2 border-t border-slate-100 text-xs text-amber-600 flex items-center gap-1">
            <AlertCircle className="h-3 w-3 shrink-0" />
            Inneholder estimerte data
          </p>
        )}
      </div>
    </Link>
  );
};

export default MunicipalityCard;
